import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { IPortalQuote, QuoteStatus } from "shared";

import { useSession } from "../../hooks/useSession";
import { useFetchContactQuery } from "../../services/api/contact";
import { LoadingFallback } from "../LoadingFallback";
import { classNames } from "../../../common/lib/classNames";
import { card } from "../portalUi";

export function Quotes() {
  const { code } = useParams();
  const session = useSession();
  const { isLoading, data: contactResult } = useFetchContactQuery({ code, sessionId: session?.id }, { skip: !code || !session?.id });
  const [currentTab, setCurrentTab] = useState<QuoteStatus>(QuoteStatus.SENT);

  const quoteList: {
    [key in QuoteStatus]?: IPortalQuote[]
  } = useMemo(() => {
    if (!contactResult || !contactResult.quotes) {
      return {
        [QuoteStatus.SENT]: [],
        [QuoteStatus.ACCEPTED]: [],
        [QuoteStatus.DECLINED]: [],
      };
    }

    const result = {
      [QuoteStatus.SENT]: [],
      [QuoteStatus.ACCEPTED]: [],
      [QuoteStatus.DECLINED]: [],
    };

    contactResult.quotes.forEach(quote => {
      if (result[quote.status]) {
        result[quote.status].push(quote);

        if (quote.status === QuoteStatus.CONVERTED) {
          // also add to accepted
          result[QuoteStatus.ACCEPTED].push(quote);
        }
      }
    });

    return result;
  }, [contactResult]);

  const tabs = [
    { status: QuoteStatus.SENT, name: `Outstanding` },
    { status: QuoteStatus.ACCEPTED, name: `Accepted` },
    { status: QuoteStatus.DECLINED, name: `Declined` },
  ];

  if (isLoading) {
    return <LoadingFallback />;
  }

  return (
    <div className={ card }>
      <div className={ `flex flex-wrap items-center justify-between gap-2.5 border-b border-gray-100 px-5 py-3` }>
        <h2 className={ `text-base font-semibold text-gray-900` }>
          { contactResult?.language?.quotes || `Quotes & Estimates` }
        </h2>
        <div className={ `flex flex-wrap gap-1` }>
          { tabs.map(tab => (
            <button
              key={ tab.status }
              type={ `button` }
              onClick={ () => setCurrentTab(tab.status) }
              className={ classNames(
                `h-[1.875rem] cursor-pointer whitespace-nowrap rounded-full px-3 text-[0.8125rem]`,
                currentTab === tab.status ? `bg-paidniceGreen-100 font-semibold text-paidniceGreen-800` : `text-gray-600 hover:bg-gray-100`,
              ) }
            >
              { `${tab.name} ${quoteList[tab.status]?.length ?? 0}` }
            </button>
          )) }
        </div>
      </div>

      { quoteList[currentTab].length === 0 && (
        <div className={ `px-5 py-4 text-sm text-gray-500` }>{ `No current quotes found` }</div>
      ) }

      { quoteList[currentTab].map(quote => (
        <Link
          key={ quote.hash }
          to={ `/q/${quote.hash}` }
          className={ `flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-100 px-5 py-4 text-gray-900 last:border-b-0 hover:bg-gray-50 hover:no-underline` }
        >
          <span className={ `flex flex-[1_1_12.5rem] flex-col gap-0.5` }>
            <span className={ `text-sm font-semibold` }>{ quote.number }</span>
            { quote.referenceText && <span className={ `text-[0.8125rem] text-gray-500` }>{ quote.referenceText }</span> }
          </span>
          <span className={ `flex flex-col gap-0.5 text-[0.8125rem] text-gray-600` }>
            { quote.issueDateString && <span>{ `Issued ${quote.issueDateString}` }</span> }
            { quote.expiryDateString && (
              <span className={ classNames(quote.hasExpired && `text-paidniceRed-700`) }>
                { `${quote.hasExpired ? `Expired` : `Expires`} ${quote.expiryDateString}` }
              </span>
            ) }
          </span>
          <span className={ `min-w-[6.25rem] text-right text-[0.9375rem] font-semibold tabular-nums` }>{ quote.amountTotalFormatted }</span>
          <span className={ `flex items-center gap-0.5 whitespace-nowrap text-[0.8125rem] font-medium text-paidniceGreen-600` }>
            { `View quote` }
            <ChevronRightIcon className={ `h-3.5 w-3.5` } />
          </span>
        </Link>
      )) }
    </div>
  );
}
